import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { ColDef } from "ag-grid-community";
import { Copy, Download, MoreHorizontal, Search, Wallet } from "lucide-react";
import { toast } from "react-toastify";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import DownloadfileModal from "../components/common/downloadfileModal";
import henceofrthEnums from "../utils/henceofrthEnums";
import { downloadExcelFile, ExportColumn } from "../components/common/ExportButton";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const transferExportColumns: ExportColumn[] = [
  { header: "Sr No.", key: "sr_no", type: "number", width: 8 },
  { header: "Transaction ID", key: "order_id", type: "text", width: 18 },
  { header: "Passenger", key: "customer_name", type: "text", width: 18 },
  { header: "Driver", key: "driver_name", type: "text", width: 18 },
  { header: "Amount", key: "amount", type: "currency", isAmount: true, width: 14 },
];

interface translist {
  order_id: string;
  customer_name: string;
  driver_name: string;
}

interface transaction {
  id: string;
  _id: string;
  booking_detail: translist;
  driver_detail: {
    name: string;
    email: string;
    profile_pic: string;
  };
  admin_earns: number;
  driver_earns: number;
  total_amount: number;
  currency: string;
  is_transferred: boolean;
  amount: number;
}

const ManageTransfer = () => {
  const location = useLocation();
  const limit = 10;
  const { authState, handlePagination, handleFilter, handleError } = useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<Array<transaction>>([]);
  const [totalCount, setTotalCount] = useState(0);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const exportData = async (start_date: number, end_date: number) => {
    try {
      let apiRes = await henceforthApi.Transaction.export(
        moment(start_date).format("DD/MM/YYYY"),
        moment(end_date).format("DD/MM/YYYY")
      );
      let data = apiRes?.data || [];

      const rows = data.map((res: any, index: number) => ({
        sr_no: index + 1,
        order_id: res?.order_id || "N/A",
        customer_name: res?.customer_name || "N/A",
        driver_name: res?.driver_name || "N/A",
        amount: Number(res?.amount || 0),
      }));

      await downloadExcelFile({
        fileName: `transfers_${moment().format("YYYY-MM-DD")}`,
        sheetName: "Transfers",
        columns: transferExportColumns,
        data: rows,
      });

      let closeModal = document.getElementById("closeModal");
      if (closeModal) {
        closeModal.click();
      }
    } catch (error: any) {
      console.error("Transfers export error:", error);
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      let apiRes = await henceforthApi.ManageTransfer.getPagnation(
        limit,
        Number(params.page) - 1,
        newParam.toString()
      );
      setTotalCount(apiRes.data.count || 0);
      setPageData(apiRes.data.data || []);
    } catch (error: any) {
      setPageData([]);
      setTotalCount(0);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, [params.page, newParam.get("search")]);

  const pay = (id: string) => {
    Swal.fire({
      title: "Do you want to pay this driver?",
      text: "This will initiate a payout to the driver's registered account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Pay Now",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        let apiRes = await henceforthApi.Payout.pay(id);
        return apiRes;
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })
      .then((result: any) => {
        if (result?.isConfirmed) {
          Swal.fire({
            title: "Payment Successful!",
            text: "Driver transfer has been processed.",
            icon: "success",
            confirmButtonText: "OK",
          });
          init();
        }
      })
      .catch((error) => {
        handleError(error);
        Swal.close();
      });
  };

  const formatCurrency = (val: number | undefined, currency: string) => {
    if (val === undefined || val === null) return "N/A";
    const symbol =
      currency === henceofrthEnums.CurrencyType.NGN
        ? "₦ "
        : currency === henceofrthEnums.CurrencyType.INR
          ? "₹ "
          : "$ ";
    return `${symbol}${val.toLocaleString()}`;
  };

  const columnDefs = useMemo<ColDef<transaction>[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "id" as any,
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Driver",
        field: "driver_detail.name" as any,
        minWidth: 200,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const detail = p.data?.driver_detail;
          const pic =
            detail?.profile_pic && detail.profile_pic !== "null"
              ? henceforthApi.FILES.imageOriginal(detail.profile_pic)
              : profile_placeholder;
          const name = detail?.name || "Not Available";
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={pic}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="min-w-0">
                <span className="font-medium text-slate-800 text-sm truncate block">
                  {name}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Driver Email",
        field: "driver_detail.email" as any,
        minWidth: 220,
        flex: 1.5,
        cellRenderer: (p: any) => {
          const email = p.data?.driver_detail?.email;
          if (!email) return <span className="text-slate-400">Not available</span>;
          return (
            <div className="flex items-center gap-1.5 py-1 text-sm text-slate-600">
              <span className="truncate max-w-[200px]">{email}</span>
            </div>
          );
        },
      },
      {
        headerName: "Admin Earn",
        field: "admin_earns",
        minWidth: 120,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-medium text-slate-700 text-sm">
              {formatCurrency(p.data?.admin_earns, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Driver Earn",
        field: "driver_earns",
        minWidth: 120,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-emerald-600 text-sm">
              {formatCurrency(p.data?.driver_earns, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Total Amount",
        field: "total_amount",
        minWidth: 120,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-900 text-sm">
              {formatCurrency(p.data?.total_amount, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Payment Status",
        field: "is_transferred",
        minWidth: 130,
        cellRenderer: (p: any) => {
          const isTransferred = p.value;
          return (
            <div className="flex items-center h-full">
              {isTransferred ? (
                <Badge variant="success">PAID</Badge>
              ) : (
                <Badge variant="warning">PENDING</Badge>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const isTransferred = row?.is_transferred;
          const rowId = row?._id || row?.id;

          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="table-action-dots p-1 text-slate-500 hover:text-slate-800 focus:outline-none"
                    aria-label="Actions"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    disabled={isTransferred}
                    onClick={() => {
                      if (!isTransferred && rowId) {
                        pay(rowId);
                      }
                    }}
                    className={`gap-2 cursor-pointer ${isTransferred ? "opacity-50 cursor-not-allowed" : "text-emerald-700 font-medium"
                      }`}
                  >
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    <span>{isTransferred ? "Already Paid" : "Pay Driver"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search driver..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Export Button */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="default"
            size="sm"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="h-9 px-4 text-sm gap-1.5 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={pageData}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No transfers found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={pageData}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      <DownloadfileModal data={exportData} />
    </>
  );
};

export default ManageTransfer;